import React, { useMemo, useState } from 'react';
import { ActionButton } from './ActionButton';
import { MID_GRAY } from '@/styles/tokens';
import { PinColorDrilldown } from './drilldowns/PinColorDrilldown';
import { PinIconDrilldown } from './drilldowns/PinIconDrilldown';
import { PinGroupDrilldown } from './drilldowns/PinGroupDrilldown';
import { PinNavigateDrilldown } from './drilldowns/PinNavigateDrilldown';
import { DeviceProviderDrilldown } from './DeviceProviderDrilldown';
import type { Component } from '@/types';
import type { MapPin } from '@/components/charts/MapComponent.types';

export interface PinContext {
  pin: {
    id: string;
    name: string;
    groupId?: string;
    navigation?: { targetPageId: string; targetPageName?: string };
    style?: { color?: string; icon?: string };
  };
  groups: Array<{ id: string; name: string; pinIds?: string[] }>;
}

interface PinActionCardProps {
  pinContext: PinContext;
  component: Component;
  pages: Array<{ id: string; name: string }>;
  currentPageId: string | null;
  updateComponent: (id: string, updates: Partial<Component>) => void;
  onAutoSend: (command: string) => void;
  onPreFill: (text: string) => void;
  onInjectMessage: (content: string) => void;
  onDataDiscovery: () => void;
  mapHomeAssistantSummary?: string | null;
  mapHomeAssistantButtonLabel?: string;
  onDeviceBinding?: () => void;
  onHomeAssistantBinding?: () => void;
  onHomeAssistantUnbind?: () => void;
  onHomeAssistantExplain?: () => void;
}

type PinCategory = 'color' | 'icon' | 'group' | 'navigate' | 'deviceProvider';

export const PinActionCard: React.FC<PinActionCardProps> = ({
  pinContext,
  component,
  pages,
  currentPageId,
  updateComponent,
  onAutoSend,
  onPreFill,
  onInjectMessage,
  onDataDiscovery,
  mapHomeAssistantSummary,
  mapHomeAssistantButtonLabel = 'Device',
  onDeviceBinding,
  onHomeAssistantBinding,
  onHomeAssistantUnbind,
  onHomeAssistantExplain,
}) => {
  const [expandedCategory, setExpandedCategory] = useState<PinCategory | null>(null);

  const closeDrilldown = () => setExpandedCategory(null);

  const openDeviceBindingFlow = () => {
    if (onHomeAssistantBinding && onDeviceBinding) {
      setExpandedCategory('deviceProvider');
      return;
    }
    if (onHomeAssistantBinding) {
      onHomeAssistantBinding();
      return;
    }
    onDeviceBinding?.();
  };

  // Look up the full pin from component properties for dataBindings check
  const pins = (component.properties as { pins?: MapPin[] }).pins;
  const fullPin = useMemo(
    () => pins?.find((p) => p.id === pinContext.pin.id),
    [pins, pinContext.pin.id],
  );
  const hasDataBindings = (fullPin?.dataBindings?.length ?? 0) > 0;

  const handleClearData = () => {
    if (!pins || !fullPin) return;
    const updatedPins = pins.map((p) =>
      p.id === fullPin.id ? { ...p, dataBindings: [] } : p,
    );
    updateComponent(component.id, {
      properties: { ...component.properties, pins: updatedPins },
    });
    onInjectMessage(`Cleared data bindings from pin "${pinContext.pin.name}".`);
  };

  const handleDeletePin = () => {
    if (!pins) return;
    const updatedPins = pins.filter((p) => p.id !== pinContext.pin.id);
    updateComponent(component.id, {
      properties: { ...component.properties, pins: updatedPins, selectedPinId: undefined },
    });
    onInjectMessage(`Deleted pin "${pinContext.pin.name}".`);
  };

  return (
    <>
      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
        ◈ <strong>Pin: &quot;{pinContext.pin.name}&quot;</strong>
      </p>
      {mapHomeAssistantSummary && (
        <p style={{ fontSize: '12px', margin: '4px 0 8px', color: MID_GRAY.DEFAULT }}>
          Map binding: {mapHomeAssistantSummary}
        </p>
      )}

      {expandedCategory === 'deviceProvider' ? (
        <DeviceProviderDrilldown
          onBack={closeDrilldown}
          onHomeAssistantBinding={onHomeAssistantBinding}
          onDeviceBinding={onDeviceBinding}
        />
      ) : expandedCategory === 'color' ? (
        <PinColorDrilldown
          onAutoSend={onAutoSend}
          onPreFill={onPreFill}
          onInjectMessage={onInjectMessage}
          onDone={closeDrilldown}
        />
      ) : expandedCategory === 'icon' ? (
        <PinIconDrilldown
          currentIcon={pinContext.pin.style?.icon}
          onAutoSend={onAutoSend}
          onDone={closeDrilldown}
        />
      ) : expandedCategory === 'group' ? (
        <PinGroupDrilldown
          pinGroupId={pinContext.pin.groupId}
          groups={pinContext.groups}
          onAutoSend={onAutoSend}
          onPreFill={onPreFill}
          onInjectMessage={onInjectMessage}
          onDone={closeDrilldown}
        />
      ) : expandedCategory === 'navigate' ? (
        <PinNavigateDrilldown
          pin={pinContext.pin}
          component={component}
          pages={pages}
          currentPageId={currentPageId}
          updateComponent={updateComponent}
          onDone={closeDrilldown}
        />
      ) : (
        <>
          <p style={{ fontSize: '13px', margin: '8px 0', color: MID_GRAY.DEFAULT }}>
            Modify this pin:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {(['color', 'icon', 'group'] as const).map((category) => (
              <ActionButton key={category} onClick={() => setExpandedCategory(category)}>
                {category === 'color' ? 'Change color' : category === 'icon' ? 'Change icon' : 'Group'}
              </ActionButton>
            ))}
            <ActionButton onClick={onDataDiscovery}>
              Data
            </ActionButton>
            {(onHomeAssistantBinding || onDeviceBinding) && (
              <ActionButton onClick={openDeviceBindingFlow}>
                {mapHomeAssistantButtonLabel}
              </ActionButton>
            )}
            {onHomeAssistantUnbind && (
              <ActionButton variant="destructive" onClick={onHomeAssistantUnbind}>
                Remove Binding
              </ActionButton>
            )}
            {onHomeAssistantExplain && (
              <ActionButton onClick={onHomeAssistantExplain}>
                Explain Binding
              </ActionButton>
            )}
            <ActionButton onClick={() => onPreFill('rename pin to ')}>
              Rename
            </ActionButton>
            <ActionButton onClick={() => setExpandedCategory('navigate')}>
              Navigate
            </ActionButton>
            {hasDataBindings && (
              <ActionButton variant="destructive" onClick={handleClearData}>
                Clear Data
              </ActionButton>
            )}
            <ActionButton variant="destructive" onClick={handleDeletePin}>
              Delete Pin
            </ActionButton>
          </div>
        </>
      )}
    </>
  );
};
